import React, { useState, useEffect } from 'react';
import {
    Plus, Trash2, ChevronUp, ChevronDown, GripVertical,
    BookOpen, Brain, CheckSquare, FileText, Upload, PenTool,
    ChevronDown as ExpandIcon, ChevronRight, FormInput
} from 'lucide-react';
import QuizBuilder from './QuizBuilder';
import TrainingBuilder from './TrainingBuilder';
import ChecklistBuilder from './ChecklistBuilder';
import FormFieldsBuilder from './FormFieldsBuilder';

// Section type definitions
const SECTION_TYPES = [
    { type: 'training', label: 'Training', icon: BookOpen, color: 'blue', description: 'Add training content with videos, PDFs, and text' },
    { type: 'quiz', label: 'Quiz', icon: Brain, color: 'green', description: 'Add quiz questions to test knowledge' },
    { type: 'checklist', label: 'Checklist', icon: CheckSquare, color: 'amber', description: 'Add a checklist of items to complete' },
    { type: 'form_fields', label: 'Form Fields', icon: FormInput, color: 'indigo', description: 'Add custom text inputs for employee responses' },
    { type: 'content', label: 'Content', icon: FileText, color: 'gray', description: 'Add simple text content or instructions' },
    { type: 'proof_upload', label: 'Proof Upload', icon: Upload, color: 'purple', description: 'Require file upload as proof of completion' },
    { type: 'signature', label: 'Signature', icon: PenTool, color: 'rose', description: 'Require digital signature acknowledgment' }
];

const CombinedTaskBuilder = ({ sections = [], onChange, onContentChange }) => {
    const [expandedSections, setExpandedSections] = useState({});
    const [showAddMenu, setShowAddMenu] = useState(false);

    // Auto-expand sections when they are loaded for the first time
    useEffect(() => {
        if (sections && sections.length > 0 && Object.keys(expandedSections).length === 0) {
            const expanded = {};
            sections.forEach((_, idx) => { expanded[idx] = true; });
            setExpandedSections(expanded);
        }
    }, [sections?.length]);

    const handleSectionsChange = (updatedSections) => {
        onChange(updatedSections);
    };

    const addSection = (sectionType) => {
        const newSection = {
            id: Date.now(),
            section_type: sectionType,
            title: `${SECTION_TYPES.find(t => t.type === sectionType)?.label || 'Section'} ${sections.length + 1}`,
            description: '',
            content: getDefaultContent(sectionType),
            is_required: true,
            passing_score: sectionType === 'quiz' ? 70 : null,
            grading_type: sectionType === 'quiz' ? 'auto' : null
        };

        const updated = [...sections, newSection];
        handleSectionsChange(updated);
        setExpandedSections(prev => ({ ...prev, [updated.length - 1]: true }));
        setShowAddMenu(false);
        onContentChange?.('combined_sections', `Added ${SECTION_TYPES.find(t => t.type === sectionType)?.label || 'section'} section`);
    };

    const getDefaultContent = (sectionType) => {
        switch (sectionType) {
            case 'quiz':
                return { questions: [] };
            case 'training':
                return { sections: [] };
            case 'checklist':
                return { items: [] };
            case 'form_fields':
                return { fields: [], instructions: '' };
            case 'content':
                return '';
            case 'proof_upload':
                return { instructions: '', allowed_types: ['image', 'pdf', 'document'] };
            case 'signature':
                return { acknowledgment_text: '' };
            default:
                return null;
        }
    };

    const updateSection = (index, field, value) => {
        const updated = [...sections];
        updated[index] = { ...updated[index], [field]: value };
        handleSectionsChange(updated);

        let valueDisplay = value;
        if (typeof value === 'string' && value.length > 50) {
            valueDisplay = value.substring(0, 50) + '...';
        }
        onContentChange?.('combined_sections', `Updated ${field} for section ${index + 1} to: "${valueDisplay}"`);
    };

    const updateSectionContent = (index, content) => {
        const updated = [...sections];
        updated[index] = { ...updated[index], content };
        handleSectionsChange(updated);
    };

    const deleteSection = (index) => {
        const sectionTitle = sections[index]?.title || `Section ${index + 1}`;
        const updated = sections.filter((_, i) => i !== index);
        handleSectionsChange(updated);
        onContentChange?.('combined_sections', `Deleted section: ${sectionTitle}`);
    };

    const moveSection = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < sections.length) {
            const updated = [...sections];
            [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
            handleSectionsChange(updated);
            // Update expanded state
            setExpandedSections(prev => ({
                ...prev,
                [index]: prev[newIndex],
                [newIndex]: prev[index]
            }));
        }
    };

    const toggleExpand = (index) => {
        setExpandedSections(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const getSectionTypeConfig = (type) => {
        return SECTION_TYPES.find(t => t.type === type) || SECTION_TYPES[0];
    };

    const renderSectionBuilder = (section, index) => {
        switch (section.section_type) {
            case 'quiz':
                return (
                    <QuizBuilder
                        content={section.content}
                        onChange={(content) => updateSectionContent(index, typeof content === 'string' ? JSON.parse(content) : content)}
                        gradingType={section.grading_type}
                        onContentChange={(field, desc) => onContentChange?.(field, `(Section ${index + 1}) ${desc}`)}
                    />
                );
            case 'training':
                return (
                    <TrainingBuilder
                        content={section.content}
                        onChange={(content) => updateSectionContent(index, content)}
                        onContentChange={(field, desc) => onContentChange?.(field, `(Section ${index + 1}) ${desc}`)}
                    />
                );
            case 'checklist':
                return (
                    <ChecklistBuilder
                        content={section.content}
                        onChange={(content) => updateSectionContent(index, typeof content === 'string' ? JSON.parse(content) : content)}
                        onContentChange={(field, desc) => onContentChange?.(field, `(Section ${index + 1}) ${desc}`)}
                    />
                );
            case 'form_fields':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Instructions (optional)</label>
                            <textarea
                                value={section.content?.instructions || ''}
                                onChange={(e) => updateSectionContent(index, { ...section.content, instructions: e.target.value })}
                                placeholder="Provide instructions for filling out the form..."
                                rows="2"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>
                        <FormFieldsBuilder
                            fields={section.content?.fields || []}
                            onChange={(fields) => updateSectionContent(index, { ...section.content, fields })}
                            onContentChange={(field, desc) => onContentChange?.(field, `(Section ${index + 1}) ${desc}`)}
                        />
                    </div>
                );
            case 'content':
                return (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Content</label>
                        <textarea
                            value={typeof section.content === 'string' ? section.content : ''}
                            onChange={(e) => updateSectionContent(index, e.target.value)}
                            placeholder="Enter content or instructions..."
                            rows="6"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                    </div>
                );
            case 'proof_upload':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Upload Instructions</label>
                            <textarea
                                value={section.content?.instructions || ''}
                                onChange={(e) => updateSectionContent(index, { ...section.content, instructions: e.target.value })}
                                placeholder="Describe what the employee should upload as proof..."
                                rows="3"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Allowed File Types</label>
                            <div className="flex flex-wrap gap-2">
                                {['image', 'pdf', 'document', 'video'].map(type => (
                                    <label key={type} className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-slate-800/50 rounded-lg cursor-pointer hover:bg-gray-100 dark:bg-slate-800">
                                        <input
                                            type="checkbox"
                                            checked={section.content?.allowed_types?.includes(type) || false}
                                            onChange={(e) => {
                                                const types = section.content?.allowed_types || [];
                                                const updated = e.target.checked
                                                    ? [...types, type]
                                                    : types.filter(t => t !== type);
                                                updateSectionContent(index, { ...section.content, allowed_types: updated });
                                            }}
                                            className="w-4 h-4 text-primary rounded"
                                        />
                                        <span className="text-sm capitalize">{type}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            case 'signature':
                return (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Acknowledgment Text</label>
                        <textarea
                            value={section.content?.acknowledgment_text || ''}
                            onChange={(e) => updateSectionContent(index, { acknowledgment_text: e.target.value })}
                            placeholder="Enter the text the employee must acknowledge by signing..."
                            rows="4"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                            The employee will see this text and must digitally sign to acknowledge.
                        </p>
                    </div>
                );
            default:
                return <p className="text-gray-500 dark:text-slate-400">Unknown section type</p>;
        }
    };

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Task Builder</h3>
                    <p className="text-sm text-gray-500 dark:text-slate-400">Add sections to create your task</p>
                </div>
            </div>

            {/* Section List */}
            {sections.length === 0 ? (
                <div className="bg-gray-50 dark:bg-slate-800/50 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-8 text-center">
                    <p className="text-gray-600 dark:text-slate-300 mb-4">No sections yet. Add sections to build your task.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {sections.map((section, index) => {
                        const config = getSectionTypeConfig(section.section_type);
                        const Icon = config.icon;
                        const isExpanded = expandedSections[index];

                        return (
                            <div
                                key={section.id || index}
                                className={`border rounded-xl overflow-hidden transition-all ${isExpanded ? 'border-gray-300 dark:border-slate-600 shadow-sm' : 'border-gray-200 dark:border-slate-700'
                                    }`}
                            >
                                {/* Section Header */}
                                <div
                                    className={`flex items-center gap-3 p-4 cursor-pointer ${isExpanded ? 'bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200' : 'bg-white dark:bg-slate-900 hover:bg-gray-50 dark:bg-slate-800/50'
                                        }`}
                                    onClick={() => toggleExpand(index)}
                                >
                                    {/* Drag Handle & Reorder */}
                                    <div className="flex flex-col gap-0.5" onClick={e => e.stopPropagation()}>
                                        <button
                                            type="button"
                                            onClick={() => moveSection(index, 'up')}
                                            disabled={index === 0}
                                            className="p-0.5 text-gray-400 hover:text-gray-600 dark:text-slate-300 disabled:opacity-30"
                                        >
                                            <ChevronUp className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => moveSection(index, 'down')}
                                            disabled={index === sections.length - 1}
                                            className="p-0.5 text-gray-400 hover:text-gray-600 dark:text-slate-300 disabled:opacity-30"
                                        >
                                            <ChevronDown className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {/* Section Type Badge */}
                                    <div className={`p-2 rounded-lg bg-${config.color}-100`}>
                                        <Icon className={`w-5 h-5 text-${config.color}-600`} />
                                    </div>

                                    {/* Section Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-gray-500 dark:text-slate-400">Section {index + 1}</span>
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-700`}>
                                                {config.label}
                                            </span>
                                            {section.is_required && (
                                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-500/20 text-red-700">
                                                    Required
                                                </span>
                                            )}
                                        </div>
                                        <input
                                            type="text"
                                            value={section.title}
                                            onChange={(e) => updateSection(index, 'title', e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="font-medium text-gray-900 dark:text-white bg-transparent border-none focus:ring-0 p-0 w-full"
                                            placeholder="Section title..."
                                        />
                                    </div>

                                    {/* Expand/Collapse & Delete */}
                                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                        <button
                                            type="button"
                                            onClick={() => deleteSection(index)}
                                            className="p-2 text-red-600 hover:bg-red-50 dark:bg-red-500/10 rounded-lg transition-colors"
                                            title="Delete Section"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => toggleExpand(index)}
                                            className="p-2 text-gray-400 hover:text-gray-600 dark:text-slate-300 rounded-lg transition-colors"
                                        >
                                            {isExpanded ? <ExpandIcon className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>

                                {/* Section Content (Expanded) */}
                                {isExpanded && (
                                    <div className="p-4 bg-white dark:bg-slate-900">
                                        {/* Section Settings */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 pb-4 border-b border-gray-100 dark:border-slate-700/50">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Description</label>
                                                <input
                                                    type="text"
                                                    value={section.description || ''}
                                                    onChange={(e) => updateSection(index, 'description', e.target.value)}
                                                    placeholder="Brief description of this section..."
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                                                />
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={section.is_required}
                                                        onChange={(e) => updateSection(index, 'is_required', e.target.checked)}
                                                        className="w-4 h-4 text-primary rounded"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-slate-200">Required</span>
                                                </label>
                                                {section.section_type === 'quiz' && (
                                                    <>
                                                        <div className="flex items-center gap-2">
                                                            <label className="text-sm text-gray-700 dark:text-slate-200">Grading:</label>
                                                            <select
                                                                value={section.grading_type || 'auto'}
                                                                onChange={(e) => updateSection(index, 'grading_type', e.target.value)}
                                                                className="px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-900 focus:outline-none focus:ring-1 focus:ring-primary"
                                                            >
                                                                <option value="auto">Auto-Graded</option>
                                                                <option value="manual">Manual Review</option>
                                                            </select>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <label className="text-sm text-gray-700 dark:text-slate-200">Pass Score:</label>
                                                            <input
                                                                type="number"
                                                                value={section.passing_score || 70}
                                                                onChange={(e) => updateSection(index, 'passing_score', parseInt(e.target.value))}
                                                                min="0"
                                                                max="100"
                                                                className="w-16 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-sm"
                                                            />
                                                            <span className="text-sm text-gray-500 dark:text-slate-400">%</span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Section-specific Builder */}
                                        {renderSectionBuilder(section, index)}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Add Section Button */}
            <div className="relative">
                <button
                    type="button"
                    onClick={() => setShowAddMenu(!showAddMenu)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl text-gray-600 dark:text-slate-300 hover:border-primary hover:text-primary hover:bg-primary/5 transition-all font-medium"
                >
                    <Plus className="w-5 h-5" />
                    Add Section
                </button>

                {/* Section Type Menu */}
                {showAddMenu && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl shadow-lg z-10 p-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                        {SECTION_TYPES.map(type => {
                            const Icon = type.icon;
                            return (
                                <button
                                    key={type.type}
                                    type="button"
                                    onClick={() => addSection(type.type)}
                                    className={`flex items-center gap-3 p-3 rounded-lg hover:bg-${type.color}-50 transition-colors text-left`}
                                >
                                    <div className={`p-2 rounded-lg bg-${type.color}-100`}>
                                        <Icon className={`w-5 h-5 text-${type.color}-600`} />
                                    </div>
                                    <div>
                                        <div className="font-medium text-gray-900 dark:text-white text-sm">{type.label}</div>
                                        <div className="text-xs text-gray-500 dark:text-slate-400">{type.description}</div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Summary */}
            {sections.length > 0 && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                    <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-primary">
                            Total Sections: {sections.length}
                        </span>
                        <span className="font-medium text-primary">
                            Required: {sections.filter(s => s.is_required).length}
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {sections.map((section, idx) => {
                            const config = getSectionTypeConfig(section.section_type);
                            return (
                                <span
                                    key={idx}
                                    className={`px-2 py-1 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-700`}
                                >
                                    {idx + 1}. {config.label}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CombinedTaskBuilder;
